<script setup lang="ts">
/**
 * 登录 / 免费开通弹窗（取代原全屏 LoginView）。从侧栏底部登录栏或内容区欢迎门唤起。
 *
 * 四态：login（手机号+密码）/ pick（多档口选档）/ change（首登强制改密）/
 *       free（免费档自助：appid+AppSecret+档口名+手机号）。
 * 成功后 refreshMe + 关弹窗，内容区随 isAuthenticated 自动显示。
 */
import { ref, watch } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useAuthModal } from '@/composables/useAuthModal'
import { ApiError } from '@/api/client'
import {
  login as apiLogin,
  changePassword as apiChangePassword,
  isPickTenant,
  type TenantCandidate
} from '@/api/auth'
import { freeBind } from '@/api/onboarding'

const auth = useAuthStore()
const { open, initialMode, closeAuth } = useAuthModal()

type Mode = 'login' | 'pick' | 'change' | 'free'
const mode = ref<Mode>('login')

const phone = ref('')
const password = ref('')
const submitting = ref(false)
const errMsg = ref<string | null>(null)
const candidates = ref<TenantCandidate[]>([])

const newPassword = ref('')
const confirmPassword = ref('')
const oldForChange = ref('')

// 免费档
const fAppid = ref('')
const fSecret = ref('')
const fShop = ref('')
const fPhone = ref('')

// 打开时按 initialMode 切到登录或免费;若已登录但 must_change → 改密
watch(open, (v) => {
  if (!v) return
  errMsg.value = null
  if (auth.isAuthenticated && auth.mustChangePassword) {
    mode.value = 'change'
  } else {
    mode.value = initialMode.value
  }
})

function msgFromErr(e: unknown): string {
  if (e instanceof ApiError) return e.message
  return e instanceof Error ? e.message : '网络异常,请重试'
}

function switchMode(m: Mode) {
  errMsg.value = null
  mode.value = m
}

async function done() {
  await auth.refreshMe()
  closeAuth()
}

async function enter(tenantUlid?: string) {
  errMsg.value = null
  const p = phone.value.trim()
  if (!p || !password.value) {
    errMsg.value = '请填手机号和密码'
    return
  }
  submitting.value = true
  try {
    const resp = await apiLogin(p, password.value, tenantUlid)
    if (isPickTenant(resp)) {
      candidates.value = resp.candidates
      mode.value = 'pick'
      return
    }
    auth.afterLogin(resp.token, {
      user: resp.user,
      tenant: resp.tenant,
      must_change_password: resp.must_change_password
    })
    if (resp.must_change_password) {
      oldForChange.value = password.value
      mode.value = 'change'
      return
    }
    await done()
  } catch (e: unknown) {
    errMsg.value = msgFromErr(e)
  } finally {
    submitting.value = false
  }
}

function pick(c: TenantCandidate) {
  void enter(c.tenant_ulid)
}

async function submitChange() {
  errMsg.value = null
  if (newPassword.value.length < 8) {
    errMsg.value = '新密码至少 8 位'
    return
  }
  if (newPassword.value !== confirmPassword.value) {
    errMsg.value = '两次新密码不一致'
    return
  }
  if (!oldForChange.value) {
    errMsg.value = '请填原密码'
    return
  }
  submitting.value = true
  try {
    await apiChangePassword(oldForChange.value, newPassword.value)
    auth.clearMustChange()
    await done()
  } catch (e: unknown) {
    errMsg.value = msgFromErr(e)
  } finally {
    submitting.value = false
  }
}

async function submitFree() {
  errMsg.value = null
  const appid = fAppid.value.trim()
  const secret = fSecret.value.trim()
  if (!appid || !secret) {
    errMsg.value = '请填小程序 AppID 和 AppSecret'
    return
  }
  if (!fShop.value.trim() || !fPhone.value.trim()) {
    errMsg.value = '请填档口名称和手机号'
    return
  }
  submitting.value = true
  try {
    const resp = await freeBind({
      appid,
      app_secret: secret,
      shop_name: fShop.value.trim(),
      phone: fPhone.value.trim()
    })
    auth.afterFreeBind(resp.token)
    await done()
  } catch (e: unknown) {
    errMsg.value = msgFromErr(e)
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <div v-if="open" class="modal-mask" @click.self="mode !== 'change' && closeAuth()" v-no-backdrop-close>
    <div class="modal-card">
      <button v-if="mode !== 'change'" class="modal-close" type="button" @click="closeAuth">
        <i class="ti ti-x" aria-hidden="true"></i>
      </button>

      <div class="brand">
        <span class="brand-badge"><i class="ti ti-photo-search" aria-hidden="true"></i></span>
        <div class="brand-text">
          <div class="brand-title">面料档口管理台</div>
          <div class="brand-sub">登录账号，或免费开通试用</div>
        </div>
      </div>

      <!-- 登录 -->
      <form v-if="mode === 'login'" class="form" @submit.prevent="enter()">
        <label class="field">
          <span class="field-label">手机号</span>
          <input v-model="phone" class="inp" type="text" inputmode="tel" autocomplete="username" placeholder="登录手机号" maxlength="20" />
        </label>
        <label class="field">
          <span class="field-label">密码</span>
          <input v-model="password" class="inp" type="password" autocomplete="current-password" placeholder="密码" @keyup.enter="enter()" />
        </label>
        <div v-if="errMsg" class="err">{{ errMsg }}</div>
        <button class="btn-primary" type="submit" :disabled="submitting">{{ submitting ? '登录中…' : '登录' }}</button>
        <div class="divider"><span>没有账号？</span></div>
        <button class="btn-ghost" type="button" @click="switchMode('free')">
          <i class="ti ti-gift" aria-hidden="true"></i> 免费试用（用小程序 AppID 开通）
        </button>
      </form>

      <!-- 免费档 -->
      <form v-else-if="mode === 'free'" class="form" @submit.prevent="submitFree">
        <div class="pick-title">免费开通：填你的小程序信息（200 张 · 仅展示，搜图/进销存需付费开通）</div>
        <label class="field">
          <span class="field-label">小程序 AppID</span>
          <input v-model="fAppid" class="inp" type="text" placeholder="wx 开头，小程序后台可查" />
        </label>
        <label class="field">
          <span class="field-label">AppSecret（密钥）</span>
          <input v-model="fSecret" class="inp" type="password" placeholder="小程序后台 → 开发管理 → 开发设置" />
        </label>
        <label class="field">
          <span class="field-label">档口名称</span>
          <input v-model="fShop" class="inp" type="text" placeholder="你的店名" maxlength="40" />
        </label>
        <label class="field">
          <span class="field-label">手机号</span>
          <input v-model="fPhone" class="inp" type="text" inputmode="tel" placeholder="联系手机号（以后升级也用它）" maxlength="20" />
        </label>
        <div v-if="errMsg" class="err">{{ errMsg }}</div>
        <button class="btn-primary" type="submit" :disabled="submitting">{{ submitting ? '开通中…' : '免费开通并进入' }}</button>
        <button class="btn-link" type="button" @click="switchMode('login')">已有账号？去登录</button>
        <div class="hint">AppSecret 仅用于验证小程序归属 + 让小程序能上线，加密保存。服务器 IP 需在小程序后台白名单内。</div>
      </form>

      <!-- 选档口 -->
      <div v-else-if="mode === 'pick'" class="form">
        <div class="pick-title">该手机号属于多个档口，请选择：</div>
        <button v-for="c in candidates" :key="c.tenant_ulid" class="pick-item" type="button" :disabled="submitting" @click="pick(c)">
          <span class="pick-avatar">{{ c.shop_name.charAt(0) }}</span>
          <span class="pick-name">{{ c.shop_name }}</span>
          <i class="ti ti-chevron-right" aria-hidden="true"></i>
        </button>
        <div v-if="errMsg" class="err">{{ errMsg }}</div>
        <button class="btn-link" type="button" @click="switchMode('login')">返回</button>
      </div>

      <!-- 改密 -->
      <form v-else class="form" @submit.prevent="submitChange">
        <div class="pick-title">设置新密码（首次登录需修改）</div>
        <label v-if="!oldForChange" class="field">
          <span class="field-label">原密码</span>
          <input v-model="oldForChange" class="inp" type="password" placeholder="原 / 初始密码" />
        </label>
        <label class="field">
          <span class="field-label">新密码</span>
          <input v-model="newPassword" class="inp" type="password" autocomplete="new-password" placeholder="至少 8 位" />
        </label>
        <label class="field">
          <span class="field-label">确认新密码</span>
          <input v-model="confirmPassword" class="inp" type="password" autocomplete="new-password" placeholder="再输一次" @keyup.enter="submitChange" />
        </label>
        <div v-if="errMsg" class="err">{{ errMsg }}</div>
        <button class="btn-primary" type="submit" :disabled="submitting">{{ submitting ? '保存中…' : '设置新密码并进入' }}</button>
      </form>
    </div>
  </div>
</template>

<style scoped>
.modal-mask {
  position: fixed;
  inset: 0;
  z-index: 200;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}
.modal-card {
  position: relative;
  width: 360px;
  max-height: 90vh;
  overflow-y: auto;
  background: var(--color-background-primary);
  border: 0.5px solid var(--color-border-tertiary);
  border-radius: var(--border-radius-lg);
  padding: 26px 26px 22px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
}
.modal-close {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 26px;
  height: 26px;
  border: none;
  background: transparent;
  color: var(--color-text-tertiary);
  cursor: pointer;
  border-radius: var(--border-radius-sm, 6px);
  font-size: 18px;
}
.modal-close:hover {
  background: var(--color-background-secondary);
  color: var(--color-text-primary);
}
.brand {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
}
.brand-badge {
  width: 38px;
  height: 38px;
  border-radius: var(--border-radius-md);
  background: var(--color-brand);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
}
.brand-title {
  font-size: var(--fs-page);
  font-weight: var(--fw-bold);
  color: var(--color-text-primary);
}
.brand-sub {
  font-size: var(--fs-label);
  color: var(--color-text-tertiary);
  margin-top: 2px;
}
.form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.field {
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.field-label {
  font-size: var(--fs-label);
  color: var(--color-text-secondary);
}
.inp {
  font-size: var(--fs-item);
  padding: 9px 11px;
  border-radius: var(--border-radius-md);
  border: var(--border-width-control) solid var(--color-border-control);
  background: var(--color-background-primary);
  color: var(--color-text-primary);
  font-family: inherit;
  outline: none;
}
.inp:focus {
  border-color: var(--color-text-info);
}
.btn-primary {
  margin-top: 2px;
  padding: 10px;
  font-size: var(--fs-item);
  font-weight: var(--fw-med);
  border-radius: var(--border-radius-md);
  border: none;
  background: var(--color-brand);
  color: #fff;
  cursor: pointer;
  font-family: inherit;
}
.btn-primary:disabled {
  opacity: 0.6;
  cursor: default;
}
.btn-primary:hover:not(:disabled) {
  filter: brightness(1.06);
}
.btn-link {
  background: transparent;
  border: none;
  color: var(--color-text-secondary);
  font-size: var(--fs-control);
  cursor: pointer;
  padding: 4px;
}
.divider {
  display: flex;
  align-items: center;
  text-align: center;
  color: var(--color-text-tertiary);
  font-size: var(--fs-label);
  gap: 8px;
  margin: 2px 0;
}
.divider::before,
.divider::after {
  content: '';
  flex: 1;
  height: 0.5px;
  background: var(--color-border-tertiary);
}
.btn-ghost {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 9px;
  font-size: var(--fs-control);
  font-weight: var(--fw-med);
  border-radius: var(--border-radius-md);
  border: var(--border-width-control) solid var(--color-border-control);
  background: var(--color-background-primary);
  color: var(--color-text-primary);
  cursor: pointer;
  font-family: inherit;
}
.btn-ghost:hover {
  background: var(--color-background-secondary);
}
.err {
  font-size: var(--fs-control);
  color: var(--color-text-danger, #c0392b);
  background: var(--color-background-danger, #fdecea);
  padding: 7px 10px;
  border-radius: var(--border-radius-sm, 6px);
}
.hint {
  font-size: var(--fs-label);
  color: var(--color-text-tertiary);
  line-height: 1.5;
  margin-top: 2px;
}
.pick-title {
  font-size: var(--fs-control);
  color: var(--color-text-secondary);
}
.pick-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 12px;
  border: var(--border-width-control) solid var(--color-border-control);
  border-radius: var(--border-radius-md);
  background: var(--color-background-primary);
  cursor: pointer;
  font-family: inherit;
  color: inherit;
  text-align: left;
}
.pick-item:hover:not(:disabled) {
  background: var(--color-background-secondary);
}
.pick-avatar {
  width: 24px;
  height: 24px;
  border-radius: var(--border-radius-sm);
  background: #eeedfe;
  color: #3c3489;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: var(--fw-med);
}
.pick-name {
  flex: 1;
  font-size: var(--fs-item);
}
.pick-item .ti {
  color: var(--color-text-tertiary);
}
</style>
